import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiPhone, FiMail, FiExternalLink, FiUser, FiMapPin, FiShare2 } = FiIcons;

const RepresentativeCard = ({ rep, onShare }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-lg shadow-md p-5 hover:shadow-lg transition-shadow duration-300"
    >
      <div className="flex items-start space-x-4">
        {rep.photoUrl ? (
          <img 
            src={rep.photoUrl} 
            alt={rep.name} 
            className="w-16 h-16 rounded-full object-cover border border-gray-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(rep.name)}&background=DC2626&color=fff`;
            }}
          />
        ) : (
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiUser} className="h-8 w-8 text-gray-400" />
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-patriot-navy">{rep.name}</h3>
              <div className="text-gray-600 text-sm flex flex-wrap items-center gap-1">
                <span>{rep.position || rep.officeName}</span>
                {rep.district && (
                  <>
                    <span className="mx-1">•</span>
                    <span className="font-medium">District {rep.district}</span>
                  </>
                )}
              </div>
              {rep.party && (
                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getPartyColor(rep.party)}`}>
                  {rep.party}
                </span>
              )}
            </div>
            
            <button
              onClick={() => onShare && onShare(rep)}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              title="Share contact information"
            >
              <SafeIcon icon={FiShare2} className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          <div className="mt-4 space-y-2">
            {rep.phones && rep.phones.length > 0 && (
              <div className="flex items-center text-sm text-gray-600">
                <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2" />
                <a href={`tel:${rep.phones[0]}`} className="hover:text-patriot-red">
                  {rep.phones[0]}
                </a>
              </div>
            )}
            
            {rep.emails && rep.emails.length > 0 && (
              <div className="flex items-center text-sm text-gray-600">
                <SafeIcon icon={FiMail} className="h-4 w-4 mr-2" />
                <a href={`mailto:${rep.emails[0]}`} className="hover:text-patriot-red">
                  {rep.emails[0]}
                </a>
              </div>
            )}
            
            {rep.urls && rep.urls.length > 0 && (
              <div className="flex items-center text-sm text-gray-600">
                <SafeIcon icon={FiExternalLink} className="h-4 w-4 mr-2" />
                <a href={rep.urls[0]} target="_blank" rel="noopener noreferrer" className="hover:text-patriot-red">
                  Official Website
                </a>
              </div>
            )}
          </div>

          {rep.address && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
              <div className="flex items-center text-gray-700 mb-1">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2 text-gray-500" />
                <span className="font-medium">Office Address:</span>
              </div>
              <p className="text-gray-600 ml-6">
                {rep.address.line1} {rep.address.line2 && <>, {rep.address.line2}</>}<br />
                {rep.address.city}, {rep.address.state} {rep.address.zip}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Helper function to determine party color
const getPartyColor = (party) => {
  if (!party) return 'bg-gray-100 text-gray-600';
  
  switch (party.toLowerCase()) {
    case 'republican':
      return 'bg-patriot-red bg-opacity-10 text-patriot-red';
    case 'democrat':
    case 'democratic':
      return 'bg-blue-100 text-blue-600';
    case 'independent':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export default RepresentativeCard;